/**
 * Personal saved views (memory tier 2): a query — terms plus the chips —
 * persisted to `localStorage`, namespaced per vault id. A saved view is the
 * QUERY only, never canvas state: the graph is a product of expansions the
 * viewer's own reads performed, and replaying it is the viewer's gesture.
 *
 * PERSONAL by design — there is deliberately no share affordance here. A
 * shared view can embed a query another viewer cannot fully see (their gate
 * may answer the very same terms with fewer rows, or refuse them), and
 * presenting someone else's saved result as a view would smuggle a claim
 * about a bank the sharer's credentials shaped. Sharing is a later, separate
 * decision with its own disclosure story; this module stays per-browser.
 */

/** The query a saved view restores — structurally the section's
 *  `MemoryQuery` (terms + the three facets; `null` = unset axis). */
export interface MemoryQuerySnapshot {
  terms: string;
  type: string | null;
  privacy: string | null;
  scope: string | null;
}

export interface MemorySavedView {
  name: string;
  query: MemoryQuerySnapshot;
}

/** `vault-ui.memory.savedViews.<vaultId>` — per-vault, per-browser. */
export function savedViewsKey(vaultId: string): string {
  return `vault-ui.memory.savedViews.${vaultId}`;
}

/** localStorage or null where it is unavailable (private modes) — a view that
 *  cannot persist simply does not save, never faults the panel. */
function storage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function validView(v: unknown): v is MemorySavedView {
  if (typeof v !== 'object' || v === null) return false;
  const { name, query } = v as { name?: unknown; query?: unknown };
  if (typeof name !== 'string' || name.trim() === '' || typeof query !== 'object' || query === null) {
    return false;
  }
  const q = query as Record<string, unknown>;
  return typeof q.terms === 'string'
    && (q.type === null || typeof q.type === 'string')
    && (q.privacy === null || typeof q.privacy === 'string')
    && (q.scope === null || typeof q.scope === 'string');
}

/** The vault's saved views, oldest first. Anything unparsable or malformed
 *  in the payload is dropped silently — corrupted storage must not cost the
 *  palette its other views. */
export function loadSavedViews(vaultId: string): MemorySavedView[] {
  const s = storage();
  if (s === null) return [];
  try {
    const raw = s.getItem(savedViewsKey(vaultId));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(validView);
  } catch {
    return [];
  }
}

/** Saves (or replaces, by name) one view. Returns the resulting list so the
 *  caller re-renders from one source of truth. */
export function saveSavedView(vaultId: string, view: MemorySavedView): MemorySavedView[] {
  const name = view.name.trim();
  if (name === '') return loadSavedViews(vaultId);
  const next = [
    ...loadSavedViews(vaultId).filter((v) => v.name !== name),
    { name, query: view.query },
  ];
  try {
    storage()?.setItem(savedViewsKey(vaultId), JSON.stringify(next));
  } catch {
    // Quota/private-mode: the view is simply not persisted.
  }
  return next;
}

/** Removes one view by name; returns the resulting list. */
export function deleteSavedView(vaultId: string, name: string): MemorySavedView[] {
  const next = loadSavedViews(vaultId).filter((v) => v.name !== name);
  try {
    storage()?.setItem(savedViewsKey(vaultId), JSON.stringify(next));
  } catch {
    // Unpersisted removal still updates this session's list.
  }
  return next;
}
